import { Button, Card, Checkbox, Input, Label, RadioGroup, RadioGroupItem } from "@nfp-tracker/components";
import { getCurrentUserTimeZone, NFP_METHOD_OPTIONS } from "@nfp-tracker/utils";
import { ChevronLeft, ChevronRight, Loader2 } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Heart, User, CalendarBlank, Target, Users, EnvelopeSimple } from "phosphor-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { resetOwnerContextCache } from "../hooks/useOwnerContext";
import { useToast } from "@nfp-tracker/components";
import { getInviteRoute, ROUTES } from "../constants/routes";
import { trackEvent } from "../integrations/analytics/client";
import { supabase } from "../integrations/supabase/client";
import { resolveOwnerContext } from "../integrations/supabase/domain";
import { invokeEdgeFunction } from "../integrations/supabase/functions";
import {
  GENERIC_EXTERNAL_ERROR_MESSAGE,
  handleExternalError,
} from "../utils/errorSnackbar";
import {
  acceptSpouseInvite,
  buildInviteExpiry,
  extendInviteExpiry,
  getInviteAcceptErrorMessage,
} from "../utils/spouseInvites";

type PendingInvite = {
  id: string;
  invitee_email: string;
  token: string;
  expires_at: string;
  created_at?: string;
  inviter_user_id?: string;
  sender_name?: string;
  sender_email?: string;
};

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const isValidAge = (value: string): boolean => {
  const parsedAge = Number.parseInt(value, 10);
  return !Number.isNaN(parsedAge) && parsedAge >= 13 && parsedAge <= 80;
};

const isTransientNetworkError = (error: unknown): boolean => {
  const message = error instanceof Error ? error.message : typeof error === "string" ? error : "";
  return /load failed|failed to fetch|network request failed|networkerror/i.test(message);
};

// iOS WebView drops in-flight requests as "TypeError: Load failed" on transient
// network blips; retry those a couple times before surfacing the error.
const withNetworkRetry = async <T,>(operation: () => Promise<T>, attempts = 3): Promise<T> => {
  for (let attempt = 1; attempt <= attempts; attempt++) {
    try {
      return await operation();
    } catch (error) {
      if (attempt === attempts || !isTransientNetworkError(error)) {
        throw error;
      }
      await new Promise((resolve) => setTimeout(resolve, 500 * attempt));
    }
  }
  throw new Error("unreachable");
};

const getInviteRecipientLabel = (invite: PendingInvite | null): string => {
  if (!invite) {
    return "Unknown account";
  }

  if (invite.sender_email) {
    return invite.sender_email;
  }

  if (invite.sender_name) {
    return invite.sender_name;
  }

  if (invite.inviter_user_id) {
    return `account ${invite.inviter_user_id.slice(0, 8)}`;
  }

  return "Unknown account";
};

const Onboarding = () => {
  const [searchParams] = useSearchParams();
  const inviteToken = searchParams.get("inviteToken")?.trim() || "";
  const [step, setStep] = useState(1);
  const [sex, setSex] = useState<"male" | "female">("female");
  const [age, setAge] = useState("");
  const [goal, setGoal] = useState<"TTC" | "AP" | "Single" | "Pregnant">("TTC");
  const [methods, setMethods] = useState<string[]>(["Marquette"]);
  const [methodOther, setMethodOther] = useState("");
  const [isPostpartum, setIsPostpartum] = useState(false);
  const [spouseFirstName, setSpouseFirstName] = useState("");
  const [spouseLastName, setSpouseLastName] = useState("");
  const [spouseEmail, setSpouseEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>("");
  const [userId, setUserId] = useState<string | null>(null);
  const [initializing, setInitializing] = useState(true);
  const [isAwaitingPartner, setIsAwaitingPartner] = useState(false);
  const [pendingInvite, setPendingInvite] = useState<PendingInvite | null>(null);
  const [incomingInvite, setIncomingInvite] = useState<PendingInvite | null>(null);
  const [incomingInviteAction, setIncomingInviteAction] = useState<"accept" | "skip">("accept");
  const [senderDisplayName, setSenderDisplayName] = useState("Your spouse");
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const isMaleFlow = sex === "male";
  const hasIncomingInvite = Boolean(incomingInvite);

  // Men only onboard by inviting their wife; women complete baseline profile fields.
  const shouldShowInviteStep = hasIncomingInvite || goal !== "Single";
  const totalSteps = isMaleFlow ? 2 : shouldShowInviteStep ? 5 : 4;

  // Progress bar width calculation
  const progressPercent = Math.round((step / totalSteps) * 100);
  const onboardingFlow = isMaleFlow ? "male" : "female";

  // Funnel position only. Goal, methods and postpartum answers are health data and are
  // intentionally never sent to analytics.
  const buildOnboardingEventProps = useCallback(() => ({
    flow: onboardingFlow,
    step,
    totalSteps,
    hasIncomingInvite,
  }), [hasIncomingInvite, onboardingFlow, step, totalSteps]);

  const trackValidationFailure = (field: string, reason: string) => {
    trackEvent("onboarding_validation_failed", {
      ...buildOnboardingEventProps(),
      field,
      reason,
    });
  };

  const validateCurrentStep = (): boolean => {
    if (step === 1) {
      if (sex !== "male" && sex !== "female") {
        const message = "Please choose who this account is tracking for.";
        trackValidationFailure("sex", "missing_selection");
        setError(message);
        toast({ title: "Missing info", description: message, variant: "destructive" });
        return false;
      }

      return true;
    }

    if (isMaleFlow) {
      if (step === 2 && !hasIncomingInvite) {
        const normalizedInviteEmail = spouseEmail.trim().toLowerCase();
        if (!normalizedInviteEmail || !EMAIL_REGEX.test(normalizedInviteEmail)) {
          const message = "Please add a valid email for your wife so we can send her invitation.";
          trackValidationFailure("spouseEmail", "invalid_or_missing_email");
          setError(message);
          toast({ title: "Invite required", description: message, variant: "destructive" });
          return false;
        }
      }

      return true;
    }

    if (step === 2) {
      if (!isValidAge(age)) {
        const message = "Please enter a valid age between 13 and 80.";
        trackValidationFailure("age", "invalid_age");
        setError(message);
        toast({ title: "Missing info", description: message, variant: "destructive" });
        return false;
      }

      return true;
    }

    if (step === 3) {
      if (
        goal !== "TTC" &&
        goal !== "AP" &&
        goal !== "Single" &&
        goal !== "Pregnant"
      ) {
        const message = "Please select your current goal.";
        trackValidationFailure("goal", "missing_goal");
        setError(message);
        toast({ title: "Missing info", description: message, variant: "destructive" });
        return false;
      }

      return true;
    }

    if (step === 4) {
      if (!methods.length) {
        const message = "Please choose at least one NFP method.";
        trackValidationFailure("method", "missing_method");
        setError(message);
        toast({ title: "Missing info", description: message, variant: "destructive" });
        return false;
      }

      if (methods.includes("Other") && !methodOther.trim()) {
        const message = "Please specify your method.";
        trackValidationFailure("methodOther", "missing_method_other");
        setError(message);
        toast({ title: "Missing info", description: message, variant: "destructive" });
        return false;
      }

      return true;
    }

    if (step === 5 && !isMaleFlow && !hasIncomingInvite && spouseEmail.trim() && !EMAIL_REGEX.test(spouseEmail.trim().toLowerCase())) {
      const message = "Please enter a valid husband email or leave it blank.";
      trackValidationFailure("spouseEmail", "invalid_optional_email");
      setError(message);
      toast({ title: "Invalid email", description: message, variant: "destructive" });
      return false;
    }

    return true;
  };

  const resolveResumeStep = (params: {
    savedSex: "male" | "female" | null;
    ageValue: string;
    savedGoal: "TTC" | "AP" | "Single" | "Pregnant" | null;
    savedMethods: string[] | null;
    savedMethodOther: string;
    hasIncoming: boolean;
  }): number => {
    const {
      savedSex,
      ageValue,
      savedGoal,
      savedMethods,
      savedMethodOther,
      hasIncoming,
    } = params;

    if (savedSex !== "male" && savedSex !== "female") {
      return 1;
    }

    if (savedSex === "male") {
      return 2;
    }

    if (!isValidAge(ageValue)) {
      return 2;
    }

    if (
      savedGoal !== "TTC" &&
      savedGoal !== "AP" &&
      savedGoal !== "Single" &&
      savedGoal !== "Pregnant"
    ) {
      return 3;
    }

    if (hasIncoming) {
      return 5;
    }

    if (!savedMethods?.length) {
      return 4;
    }

    if (savedMethods.includes("Other") && !savedMethodOther.trim()) {
      return 4;
    }

    if (savedGoal === "Single") {
      return 4;
    }

    return 5;
  };

  useEffect(() => {
    if (step > totalSteps) {
      setStep(totalSteps);
    }
  }, [step, totalSteps]);

  useEffect(() => {
    if (initializing) {
      return;
    }

    trackEvent("onboarding_step_viewed", buildOnboardingEventProps());
  }, [buildOnboardingEventProps, initializing]);

  useEffect(() => {
    const ensureSession = async () => {
      const {
        data: { session },
        error: sessionError,
      } = await supabase.auth.getSession();

      if (sessionError) {
        console.error("Session fetch error:", sessionError);
      }

      if (!session) {
        toast({
          title: "Sign in required",
          description: "Please sign in to finish onboarding.",
          variant: "destructive",
        });
        navigate(ROUTES.auth, { replace: true });
        return;
      }

      const currentUserId = session.user.id;
      setUserId(currentUserId);

      try {
        const currentUserEmail = (session.user.email || "").trim().toLowerCase();
        const [{ data: tracking }, { data: account }, ctx, { data: pendingInvites }, { data: incomingInvites }] = await Promise.all([
          supabase
            .from("user_tracking_profiles")
            .select("age, goal, methods, method_other, is_postpartum")
            .eq("user_id", currentUserId)
            .maybeSingle(),
          supabase
            .from("user_accounts")
            .select("*")
            .eq("user_id", currentUserId)
            .maybeSingle(),
          resolveOwnerContext(currentUserId),
          supabase
            .from("link_invites")
            .select("id, invitee_email, token, expires_at, created_at, accepted_at")
            .eq("inviter_user_id", currentUserId)
            .eq("link_type", "spouse")
            .is("accepted_at", null)
            .order("created_at", { ascending: false })
            .limit(1),
          currentUserEmail
            ? supabase
                .from("link_invites")
                .select("id, invitee_email, token, expires_at, created_at, inviter_user_id, accepted_at")
                .ilike("invitee_email", currentUserEmail)
                .eq("link_type", "spouse")
                .is("accepted_at", null)
                // Only offer invites the accept RPC will actually take; an
                // expired or canceled one used to surface here and then fail
                // with a generic "Unable to accept invite."
                .is("canceled_at", null)
                .gt("expires_at", new Date().toISOString())
                .order("created_at", { ascending: false })
                .limit(1)
            : Promise.resolve({ data: null }),
        ]);

        // Instructor accounts have their own (much shorter) setup flow — no cycle profile.
        // With an invite token, go back to the invite page instead: it is the only place a
        // chart-sharing invite is accepted, and it forwards to setup (with the invite id) when
        // the instructor profile is still missing.
        if ((account as { role?: string } | null)?.role === "instructor") {
          navigate(inviteToken ? getInviteRoute(inviteToken) : ROUTES.instructorSetup, {
            replace: true,
          });
          return;
        }

        // A pending instructor invite is deliberately *not* a routing signal: the role is only
        // granted out of band with the service role, so an account without it has nothing to set
        // up. Once it is provisioned, the role check above takes over.

        if (ctx.ownerUserId !== currentUserId) {
          resetOwnerContextCache(queryClient);
          navigate(ROUTES.observations, { replace: true });
          return;
        }

        const savedSex =
          (account as { sex?: string } | null)?.sex === "male" ||
          (account as { sex?: string } | null)?.sex === "female"
            ? ((account as { sex?: "male" | "female" }).sex ?? null)
            : null;
        const senderName =
          `${(account as { first_name?: string | null } | null)?.first_name || ""} ${
            (account as { last_name?: string | null } | null)?.last_name || ""
          }`.trim() || "Your spouse";
        setSenderDisplayName(senderName);
        if (savedSex === "male" || savedSex === "female") {
          setSex(savedSex);
        }

        if (tracking?.age != null) {
          setAge(String(tracking.age));
        }

        const savedGoal =
          tracking?.goal === "TTC" ||
          tracking?.goal === "AP" ||
          tracking?.goal === "Single" ||
          tracking?.goal === "Pregnant"
            ? tracking.goal
            : null;
        if (savedGoal) {
          setGoal(savedGoal);
        }

        if (tracking?.methods?.length) {
          setMethods(tracking.methods);
        }

        if (tracking?.method_other) {
          setMethodOther(tracking.method_other);
        }

        if (tracking?.is_postpartum != null) {
          setIsPostpartum(Boolean(tracking.is_postpartum));
        }

        let latestIncomingInvite = incomingInvites?.[0] as PendingInvite | undefined;

        if (inviteToken) {
          const { data: tokenInviteRows, error: tokenInviteError } = await supabase
            .rpc("get_invite_by_token", { invite_token: inviteToken });

          if (!tokenInviteError && tokenInviteRows?.[0]) {
            const tokenInvite = tokenInviteRows[0] as {
              id: string;
              email: string;
              expires_at: string;
              accepted: boolean;
              owner_user_id: string;
            };

            const isNotExpired = new Date(tokenInvite.expires_at) > new Date();
            const emailMatches = tokenInvite.email.trim().toLowerCase() === currentUserEmail;
            if (!tokenInvite.accepted && isNotExpired && emailMatches) {
              latestIncomingInvite = {
                id: tokenInvite.id,
                invitee_email: tokenInvite.email,
                token: inviteToken,
                expires_at: tokenInvite.expires_at,
                inviter_user_id: tokenInvite.owner_user_id,
              };
            } else if (!emailMatches) {
              trackEvent("onboarding_invite_mismatch", {
                flow: onboardingFlow,
                hasInviteToken: true,
              });
              toast({
                title: "Invite email mismatch",
                description: "You signed in with a different email than this invite. You can continue setup and skip invite linking.",
                variant: "destructive",
              });
            }
          }
        }

        if (latestIncomingInvite?.id) {
          const { data: inviteIdentityRows } = await supabase
            .rpc("get_link_invite_identity", { p_invite_id: latestIncomingInvite.id });

          const inviteIdentity = inviteIdentityRows?.[0] as {
            inviter_user_id: string | null;
            inviter_email: string | null;
            inviter_name: string | null;
          } | undefined;

          const inviterUserId = inviteIdentity?.inviter_user_id || latestIncomingInvite.inviter_user_id;

          setIncomingInvite({
            ...latestIncomingInvite,
            inviter_user_id: inviterUserId || undefined,
            sender_name: inviteIdentity?.inviter_name || undefined,
            sender_email: inviteIdentity?.inviter_email || undefined,
          });
          setIncomingInviteAction("accept");
        } else {
          setIncomingInvite(null);
          setIncomingInviteAction("accept");
        }

        const resumeStep = resolveResumeStep({
          savedSex,
          ageValue: tracking?.age != null ? String(tracking.age) : "",
          savedGoal,
          savedMethods: tracking?.methods ?? null,
          savedMethodOther: tracking?.method_other ?? "",
          hasIncoming: Boolean(latestIncomingInvite),
        });
        setStep(resumeStep);

        if (savedSex === "male") {
          if (ctx.partnerUserId) {
            resetOwnerContextCache(queryClient);
            navigate(ROUTES.observations, { replace: true });
            return;
          }

          if (!latestIncomingInvite) {
            const latestInvite = pendingInvites?.[0] as PendingInvite | undefined;
            if (latestInvite) {
              setPendingInvite(latestInvite);
              setSpouseEmail(latestInvite.invitee_email || "");
              setIsAwaitingPartner(true);
            }
          }
        }
      } catch (loadError: unknown) {
        handleExternalError(toast, loadError, {
          logPrefix: "Error loading onboarding state:",
        });
      } finally {
        setInitializing(false);
      }
    };

    ensureSession();
  }, [inviteToken, navigate, onboardingFlow, queryClient, toast]);

  useEffect(() => {
    if (!userId || sex !== "male" || !isAwaitingPartner) {
      return;
    }

    const channel = supabase
      .channel(`onboarding-links-${userId}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "user_data_links",
          filter: `owner_user_id=eq.${userId}`,
        },
        async () => {
          const ownerCtx = await resolveOwnerContext(userId);
          if (!ownerCtx.partnerUserId) {
            return;
          }

          toast({
            title: "Connection complete",
            description: "Your wife has linked her account.",
          });
          trackEvent("invite_accept_succeeded", {
            flow: "male",
            source: "partner_completion",
          });
          resetOwnerContextCache(queryClient);
          navigate(ROUTES.observations, { replace: true });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [isAwaitingPartner, navigate, queryClient, sex, toast, userId]);

  const normalizeError = (rawError: unknown): string => {
    if (rawError && typeof rawError === "object" && "message" in rawError) {
      return (rawError as { message?: string }).message || GENERIC_EXTERNAL_ERROR_MESSAGE;
    }

    return GENERIC_EXTERNAL_ERROR_MESSAGE;
  };

  const ensureSignupSubscription = async () => {
    // Call on every platform, including iOS. The edge function creates a
    // paused Stripe trial so the user has tracked access immediately; if the
    // user later purchases via Apple IAP, the function detects the active
    // Apple billing and skips, so this is safe to run on native iOS too.
    const { data, error } = await invokeEdgeFunction("create-signup-subscription");
    if (error) {
      throw new Error(normalizeError(error));
    }

    if (data?.skipped && data.reason === "trial_exhausted") {
      throw new Error("Your free trial has ended. Please subscribe to continue.");
    }
  };

  const sendInviteEmail = async (inviteData: PendingInvite, recipientName: string) => {
    const { error: emailError } = await invokeEdgeFunction("send-invite-email", {
      body: {
        inviteId: inviteData.id,
        recipientEmail: inviteData.invitee_email,
        recipientName,
        senderName: senderDisplayName,
        inviteToken: inviteData.token,
      },
    });

    if (emailError) {
      throw emailError;
    }
  };

  const createInvite = async (inviteeEmail: string): Promise<PendingInvite> => {
    if (!userId) {
      throw new Error("Please sign in to finish onboarding.");
    }

    const normalizedInviteeEmail = inviteeEmail.trim().toLowerCase();

    const { data: existingInvites, error: existingInviteError } = await supabase
      .from("link_invites")
      .select("id, inviter_user_id, invitee_email, token, expires_at, created_at")
      .ilike("invitee_email", normalizedInviteeEmail)
      .eq("link_type", "spouse")
      .is("accepted_at", null)
      .is("canceled_at", null)
      .limit(1);

    if (existingInviteError) {
      throw existingInviteError;
    }

    const existingInvite = existingInvites?.[0] as PendingInvite | undefined;

    if (existingInvite) {
      // Our own stale invite is reusable — push the expiry out and resend it
      // rather than dead-ending onboarding on a duplicate.
      if (existingInvite.inviter_user_id === userId) {
        const refreshedExpiry = await extendInviteExpiry(existingInvite.id);
        return { ...existingInvite, expires_at: refreshedExpiry };
      }

      throw new Error("That email already has a pending spouse invite. Ask them to use that invite link or cancel it first.");
    }

    const token = crypto.randomUUID().replace(/-/g, "");
    const expiresAt = buildInviteExpiry();

    const { data: inviteData, error: inviteError } = await supabase
      .from("link_invites")
      .insert({
        inviter_user_id: userId,
        invitee_email: normalizedInviteeEmail,
        link_type: "spouse",
        access_level: "write",
        token,
        expires_at: expiresAt.toISOString(),
      })
      .select("id, invitee_email, token, expires_at, created_at")
      .single();

    if (inviteError || !inviteData) {
      throw inviteError || new Error("Unable to create invite.");
    }

    return inviteData as PendingInvite;
  };

  const handleResendInvite = async () => {
    if (!pendingInvite) {
      return;
    }

    setLoading(true);
    setError("");
    try {
      const refreshedExpiry = await extendInviteExpiry(pendingInvite.id);
      setPendingInvite({ ...pendingInvite, expires_at: refreshedExpiry });
      await sendInviteEmail(pendingInvite, spouseFirstName.trim() || "there");
      trackEvent("invite_resent", {
        flow: onboardingFlow,
      });
      toast({
        title: "Invitation resent",
        description: `We resent the invitation to ${pendingInvite.invitee_email}.`,
      });
    } catch (resendError: unknown) {
      setError(GENERIC_EXTERNAL_ERROR_MESSAGE);
      handleExternalError(toast, resendError, {
        logPrefix: "Error resending invite:",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCheckConnection = async () => {
    if (!userId) {
      return;
    }

    setLoading(true);
    try {
      const ownerCtx = await resolveOwnerContext(userId);
      if (ownerCtx.partnerUserId) {
        resetOwnerContextCache(queryClient);
        navigate(ROUTES.observations, { replace: true });
        return;
      }

      toast({
        title: "Still waiting",
        description: "Your wife has not finished signup yet. You can resend the invitation below.",
      });
    } catch (ctxError: unknown) {
      handleExternalError(toast, ctxError, {
        logPrefix: "Error checking spouse link:",
      });
    } finally {
      setLoading(false);
    }
  };

  const acceptIncomingInvite = async (invite: PendingInvite) => {
    await acceptSpouseInvite(invite.id);
  };

  const handleUseDifferentEmail = () => {
    setError("");
    setIsAwaitingPartner(false);
    setPendingInvite(null);
    trackEvent("invite_change_requested", {
      flow: onboardingFlow,
    });
    setStep(2);
  };

  const handleIncomingInviteActionChange = (action: "accept" | "skip") => {
    setIncomingInviteAction(action);
  };

  const handleNext = async () => {
    setIsAwaitingPartner(false);
    setPendingInvite(null);

    if (!validateCurrentStep()) {
      return;
    }

    if (step < totalSteps) {
      trackEvent("onboarding_step_completed", buildOnboardingEventProps());
      setError("");
      setStep(step + 1);
    } else {
      trackEvent("onboarding_step_completed", buildOnboardingEventProps());
      await handleComplete();
    }
  };

  const handleBack = () => {
    if (step > 1) {
      setError("");
      setStep(step - 1);
    }
  };

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    navigate(ROUTES.root);
  };

  const handleComplete = async () => {
    setError("");

    if (!userId) {
      const message = "Please sign in to finish onboarding.";
      trackValidationFailure("userId", "missing_session");
      setError(message);
      toast({ title: "Sign in required", description: message, variant: "destructive" });
      navigate(ROUTES.auth, { replace: true });
      return;
    }

    if (sex === "male") {
      const normalizedInviteEmail = spouseEmail.trim().toLowerCase();
      const spouseDisplayName = `${spouseFirstName} ${spouseLastName}`.trim() || "there";
      if (!hasIncomingInvite && (!normalizedInviteEmail || !EMAIL_REGEX.test(normalizedInviteEmail))) {
        const message = "Please add a valid email for your wife so we can send her invitation.";
        trackValidationFailure("spouseEmail", "invalid_or_missing_email");
        setError(message);
        toast({ title: "Invite required", description: message, variant: "destructive" });
        return;
      }

      setLoading(true);
      try {
        await withNetworkRetry(async () => {
          const { error: trackingDeleteError } = await supabase
            .from("user_tracking_profiles")
            .delete()
            .eq("user_id", userId);
          if (trackingDeleteError) {
            throw trackingDeleteError;
          }
        });

        await withNetworkRetry(async () => {
          const { error: accountError } = await supabase
            .from("user_accounts")
            .update({
              role: "viewing",
              sex: "male",
              timezone: getCurrentUserTimeZone(),
            })
            .eq("user_id", userId);
          if (accountError) {
            throw accountError;
          }
        });

        if (hasIncomingInvite && incomingInvite) {
          if (incomingInviteAction === "accept") {
            await withNetworkRetry(() => acceptIncomingInvite(incomingInvite));
            trackEvent("invite_accept_succeeded", {
              flow: onboardingFlow,
              source: "onboarding_complete",
            });
            toast({
              title: "Link complete",
              description: "Your account has been linked successfully.",
            });
          } else {
            trackEvent("invite_skipped", {
              flow: onboardingFlow,
            });
            toast({
              title: "Invite skipped",
              description: "You can accept this pending invite later.",
            });
          }
          trackEvent("onboarding_completed", {
            flow: onboardingFlow,
            completionState: incomingInviteAction === "accept" ? "linked" : "skipped_pending_invite",
          });
          resetOwnerContextCache(queryClient);
          navigate(ROUTES.observations, { replace: true });
          return;
        }

        const inviteData = await withNetworkRetry(() => createInvite(normalizedInviteEmail));
        setPendingInvite(inviteData);
        await withNetworkRetry(() => sendInviteEmail(inviteData, spouseDisplayName));
        trackEvent("invite_sent", {
          flow: onboardingFlow,
          recipientType: "wife",
        });

        setSpouseEmail(normalizedInviteEmail);
        setIsAwaitingPartner(true);
        trackEvent("onboarding_completed", {
          flow: onboardingFlow,
          completionState: "awaiting_partner",
        });

        toast({
          title: "Invitation sent",
          description: "You're all set. Once your wife signs up, you'll be able to continue into the app.",
        });
      } catch (maleOnboardingError: unknown) {
        const inviteMessage = getInviteAcceptErrorMessage(maleOnboardingError);
        setError(inviteMessage ?? GENERIC_EXTERNAL_ERROR_MESSAGE);
        handleExternalError(toast, maleOnboardingError, {
          title: inviteMessage ? "Could not accept invite" : undefined,
          message: inviteMessage,
          logPrefix: "Error completing male onboarding:",
        });
      } finally {
        setLoading(false);
      }

      return;
    }

    if (!isValidAge(age)) {
      const message = "Please enter a valid age between 13 and 80.";
      setError(message);
      toast({ title: "Missing info", description: message, variant: "destructive" });
      return;
    }

    if (methods.includes("Other") && !methodOther.trim()) {
      const message = "Please specify your method.";
      setError(message);
      toast({ title: "Missing info", description: message, variant: "destructive" });
      return;
    }

    setLoading(true);
    try {
      await withNetworkRetry(async () => {
        const { error: profileError } = await supabase
          .from("user_tracking_profiles")
          .upsert({
            user_id: userId,
            age: parseInt(age, 10),
            goal,
            methods,
            // Dual-write the legacy singular column too — old app builds (web tabs that
            // haven't refreshed, un-updated iOS/Android installs) still read `method`
            // directly until the methods-array migration's Phase 2 drops it. Remove once
            // that phase actually runs.
            method: methods[0] ?? null,
            method_other: methods.includes("Other") ? methodOther : null,
            is_postpartum: isPostpartum,
          });
        if (profileError) throw profileError;
      });

      await withNetworkRetry(async () => {
        const { error: accountError } = await supabase
          .from("user_accounts")
          .update({
            role: "tracking",
            sex: "female",
            timezone: getCurrentUserTimeZone(),
          })
          .eq("user_id", userId);
        if (accountError) throw accountError;
      });

      if (hasIncomingInvite && incomingInvite) {
        if (incomingInviteAction === "accept") {
          await withNetworkRetry(() => acceptIncomingInvite(incomingInvite));
          trackEvent("invite_accept_succeeded", {
            flow: onboardingFlow,
            source: "onboarding_complete",
          });
        } else {
          trackEvent("invite_skipped", {
            flow: onboardingFlow,
          });
        }
      }

      const spouseDisplayName = `${spouseFirstName} ${spouseLastName}`.trim() || "there";

      // Send spouse invite email when not already coming from an incoming spouse invite.
      if (!hasIncomingInvite && spouseEmail && goal !== "Single") {
        const inviteData = await withNetworkRetry(() => createInvite(spouseEmail.trim().toLowerCase()));
        setPendingInvite(inviteData);
        try {
          await withNetworkRetry(() => sendInviteEmail(inviteData, spouseDisplayName));
          trackEvent("invite_sent", {
            flow: onboardingFlow,
            recipientType: "husband",
          });
          toast({
            title: "Invitation sent!",
            description: `We've sent an invitation to ${inviteData.invitee_email}`,
          });
        } catch (emailError) {
          console.error("Failed to send email:", emailError);
          toast({
            title: "Invitation created",
            description: "We couldn't send email right now. You can resend from Settings.",
          });
        }
      }

      await withNetworkRetry(() => ensureSignupSubscription());
      trackEvent("onboarding_completed", {
        flow: onboardingFlow,
        completionState: hasIncomingInvite
          ? incomingInviteAction === "accept"
            ? "linked"
            : "skipped_pending_invite"
          : "subscription_ready",
      });

      toast({
        title: "Profile complete!",
        description: hasIncomingInvite
          ? incomingInviteAction === "accept"
            ? "Welcome to NFP Tracker. Your account is linked and ready to use."
            : "Welcome to NFP Tracker. Your pending invite was skipped for now and your account is ready."
          : "Welcome to NFP Tracker. Your account is ready.",
      });

      resetOwnerContextCache(queryClient);
      navigate(ROUTES.observations);
    } catch (error: unknown) {
      const inviteMessage = getInviteAcceptErrorMessage(error);
      setError(inviteMessage ?? GENERIC_EXTERNAL_ERROR_MESSAGE);
      handleExternalError(toast, error, {
        title: inviteMessage ? "Could not accept invite" : undefined,
        message: inviteMessage,
        logPrefix: "Error completing onboarding:",
      });
    } finally {
      setLoading(false);
    }
  };

  function renderStep() {

    const stepMeta = isMaleFlow
      ? step === 1
        ? {
            icon: <User className="h-5 w-5" />,
            title: "About you",
            description: "",
          }
        : {
            icon: <EnvelopeSimple className="h-5 w-5" />,
            title: "Invite your wife",
            description: "Add her name and email so she can complete signup and link accounts.",
          }
      : step === 1
      ? {
          icon: <User className="h-5 w-5" />,
          title: "About you",
          description: "",
        }
      : step === 2
      ? {
          icon: <CalendarBlank className="h-5 w-5" />,
          title: "Age",
          description: "Age helps us tune your charting defaults.",
        }
      : step === 3
      ? {
          icon: <Target className="h-5 w-5" />,
          title: "Your goal",
          description: "Choose your current planning goal.",
        }
      : step === 4
      ? {
          icon: <Heart className="h-5 w-5" />,
          title: "NFP method",
          description: "Select the method(s) you use today.",
        }
      : step === 5 && hasIncomingInvite
      ? {
          icon: <EnvelopeSimple className="h-5 w-5" />,
          title: "Automatic linking",
          description: "Your account already has a pending husband invite.",
        }
      : step === 5
      ? {
          icon: <Users className="h-5 w-5" />,
          title: "Invite your husband",
          description: "Add his name and email to send the invitation now, or leave email blank to invite later.",
        }
      : {
          icon: <Users className="h-5 w-5" />,
          title: "Invite your husband",
          description: "Add his name and email to send the invitation now, or leave email blank to invite later.",
        };

    return (
      <AnimatePresence mode="wait">
        <motion.div
          key={step}
          initial={{ opacity: 0, x: 16 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -16 }}
          transition={{ duration: 0.2 }}
          className="space-y-5"
        >
          <div className="text-center space-y-2">
            <div className="inline-flex items-center justify-center w-11 h-11 rounded-full bg-primary/15 text-primary">
              {stepMeta.icon}
            </div>
            <h2 className="text-2xl font-semibold">{stepMeta.title}</h2>
            <p className="text-sm text-muted-foreground">{stepMeta.description}</p>
          </div>

          {step === 1 && (
            <div className="space-y-3">
              <Label>Sex</Label>
              <RadioGroup value={sex} onValueChange={(value) => setSex(value as "male" | "female")}>
                <label htmlFor="sex-female" className="flex items-center space-x-3 rounded-lg border p-3 cursor-pointer">
                  <RadioGroupItem value="female" id="sex-female" />
                  <span className="text-sm font-medium leading-none">Female</span>
                </label>
                <label htmlFor="sex-male" className="flex items-center space-x-3 rounded-lg border p-3 cursor-pointer">
                  <RadioGroupItem value="male" id="sex-male" />
                  <span className="text-sm font-medium leading-none">Male</span>
                </label>
              </RadioGroup>
            </div>
          )}

          {step === 2 && isMaleFlow && !hasIncomingInvite && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="spouse-first-name-male">Wife's first name</Label>
                  <Input
                    id="spouse-first-name-male"
                    value={spouseFirstName}
                    onChange={(e) => setSpouseFirstName(e.target.value)}
                    placeholder="First name"
                    className="h-12"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="spouse-last-name-male">Wife's last name</Label>
                  <Input
                    id="spouse-last-name-male"
                    value={spouseLastName}
                    onChange={(e) => setSpouseLastName(e.target.value)}
                    placeholder="Last name"
                    className="h-12"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="spouse-email-male">Wife's email</Label>
                <Input
                  id="spouse-email-male"
                  type="email"
                  value={spouseEmail}
                  onChange={(e) => setSpouseEmail(e.target.value)}
                  placeholder="name@example.com"
                  className="h-12"
                />
                <p className="text-xs text-muted-foreground">
                  Required. We'll send this invite so your wife can complete setup.
                </p>
              </div>
            </div>
          )}

          {step === 2 && isMaleFlow && hasIncomingInvite && (
            <div className="space-y-3 rounded-lg border p-4 bg-muted/30">
              <p className="text-sm">
                A pending wife invite was found for your email. Choose whether to apply it now or skip it for later.
              </p>
              <p className="text-sm text-muted-foreground">
                {incomingInvite?.sender_name
                  ? `Inviter: ${incomingInvite.sender_name}`
                  : `Inviter account: ${getInviteRecipientLabel(incomingInvite)}`}
                {incomingInvite?.sender_email ? ` (${incomingInvite.sender_email})` : ""}
              </p>
              <p className="text-xs text-muted-foreground">
                Recipient account: {getInviteRecipientLabel(incomingInvite)}
              </p>
              <div className="flex items-center space-x-3 rounded-lg border bg-background/70 p-3">
                <Checkbox
                  id="incoming-invite-toggle-male"
                  checked={incomingInviteAction === "accept"}
                  onCheckedChange={(checked) =>
                    handleIncomingInviteActionChange(checked ? "accept" : "skip")
                  }
                  disabled={loading}
                />
                <div className="space-y-1">
                  <Label htmlFor="incoming-invite-toggle-male" className="cursor-pointer">
                    {incomingInviteAction === "accept"
                      ? `Grant access to ${getInviteRecipientLabel(incomingInvite)}`
                      : "Skip for now"}
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    {incomingInviteAction === "accept"
                      ? `Completing onboarding will grant ${getInviteRecipientLabel(incomingInvite)} access to linked chart data.`
                      : "This invite remains pending and can be accepted later."}
                  </p>
                </div>
              </div>
            </div>
          )}

          {step === 2 && !isMaleFlow && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="age">Your age</Label>
                <Input
                  id="age"
                  type="number"
                  inputMode="numeric"
                  min={13}
                  max={80}
                  value={age}
                  onChange={(e) => setAge(e.target.value)}
                  placeholder="Enter age"
                  className="h-12"
                />
              </div>
            </div>
          )}

          {step === 3 && !isMaleFlow && (
            <div className="space-y-3">
              <Label>Current goal</Label>
              <RadioGroup
                value={goal}
                onValueChange={(value) =>
                  setGoal(value as "TTC" | "AP" | "Single" | "Pregnant")
                }
              >
                <label htmlFor="goal-ttc" className="flex items-center space-x-3 rounded-lg border p-3 cursor-pointer">
                  <RadioGroupItem value="TTC" id="goal-ttc" />
                  <span className="text-sm font-medium leading-none">Trying to conceive</span>
                </label>
                <label htmlFor="goal-ap" className="flex items-center space-x-3 rounded-lg border p-3 cursor-pointer">
                  <RadioGroupItem value="AP" id="goal-ap" />
                  <span className="text-sm font-medium leading-none">Avoiding pregnancy</span>
                </label>
                <label htmlFor="goal-single" className="flex items-center space-x-3 rounded-lg border p-3 cursor-pointer">
                  <RadioGroupItem value="Single" id="goal-single" />
                  <span className="text-sm font-medium leading-none">
                    Single user (no husband invite)
                  </span>
                </label>
                <label htmlFor="goal-pregnant" className="flex items-center space-x-3 rounded-lg border p-3 cursor-pointer">
                  <RadioGroupItem value="Pregnant" id="goal-pregnant" />
                  <span className="text-sm font-medium leading-none">
                    Currently pregnant (select method + optional husband invite)
                  </span>
                </label>
              </RadioGroup>
              <div className="flex items-center space-x-3 rounded-lg border bg-background/70 p-3">
                <Checkbox
                  id="postpartum-toggle"
                  checked={isPostpartum}
                  onCheckedChange={(checked) => setIsPostpartum(Boolean(checked))}
                  disabled={loading}
                />
                <div className="space-y-1">
                  <Label htmlFor="postpartum-toggle" className="cursor-pointer">
                    I am currently postpartum
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    This enables postpartum-aware charting defaults.
                  </p>
                </div>
              </div>
            </div>
          )}

          {step === 4 && !isMaleFlow && (
            <div className="space-y-3">
              <Label>NFP method(s)</Label>
              <p className="text-xs text-muted-foreground">
                Select every method you use — you can combine, e.g., Marquette monitor
                readings with Creighton mucus observations.
              </p>
              <div className="space-y-2">
                {NFP_METHOD_OPTIONS.map((option) => (
                  <label
                    key={option.value}
                    htmlFor={`method-${option.value}`}
                    className="flex items-center space-x-3 rounded-lg border p-3 cursor-pointer"
                  >
                    <Checkbox
                      id={`method-${option.value}`}
                      checked={methods.includes(option.value)}
                      onCheckedChange={(checked) =>
                        setMethods((current) =>
                          checked
                            ? [...current, option.value]
                            : current.filter((m) => m !== option.value)
                        )
                      }
                    />
                    <span className="text-sm font-medium leading-none">{option.label}</span>
                  </label>
                ))}
              </div>
              {methods.includes("Other") && (
                <div className="space-y-2">
                  <Label htmlFor="method-other">Method name</Label>
                  <Input
                    id="method-other"
                    value={methodOther}
                    onChange={(e) => setMethodOther(e.target.value)}
                    placeholder="Enter your method"
                    className="h-12"
                  />
                </div>
              )}
            </div>
          )}

          {step === 5 &&
            !isMaleFlow &&
            goal !== "Single" &&
            !hasIncomingInvite && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="spouse-first-name">Husband's first name</Label>
                  <Input
                    id="spouse-first-name"
                    value={spouseFirstName}
                    onChange={(e) => setSpouseFirstName(e.target.value)}
                    placeholder="First name"
                    className="h-12"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="spouse-last-name">Husband's last name</Label>
                  <Input
                    id="spouse-last-name"
                    value={spouseLastName}
                    onChange={(e) => setSpouseLastName(e.target.value)}
                    placeholder="Last name"
                    className="h-12"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="spouse-email">Husband's email</Label>
                <Input
                  id="spouse-email"
                  type="email"
                  value={spouseEmail}
                  onChange={(e) => setSpouseEmail(e.target.value)}
                  placeholder="name@example.com"
                  className="h-12"
                />
                <p className="text-xs text-muted-foreground">
                  Optional. Leave blank to invite later from Settings.
                </p>
              </div>
            </div>
          )}

          {step === 5 && !isMaleFlow && hasIncomingInvite && (
            <div className="space-y-3 rounded-lg border p-4 bg-muted/30">
              <p className="text-sm">
                You already have a pending husband invitation. Choose whether to apply it now or skip it for later.
              </p>
              <p className="text-sm text-muted-foreground">
                {incomingInvite?.sender_name
                  ? `Inviter: ${incomingInvite.sender_name}`
                  : `Inviter account: ${getInviteRecipientLabel(incomingInvite)}`}
                {incomingInvite?.sender_email ? ` (${incomingInvite.sender_email})` : ""}
              </p>
              <p className="text-xs text-muted-foreground">
                Recipient account: {getInviteRecipientLabel(incomingInvite)}
              </p>
              <div className="flex items-start space-x-3 rounded-lg border bg-background/70 p-3">
                <Checkbox
                  id="incoming-invite-toggle-female"
                  checked={incomingInviteAction === "accept"}
                  onCheckedChange={(checked) =>
                    handleIncomingInviteActionChange(checked ? "accept" : "skip")
                  }
                  disabled={loading}
                />
                <div className="space-y-1">
                  <Label htmlFor="incoming-invite-toggle-female" className="cursor-pointer">
                    {incomingInviteAction === "accept"
                      ? `Grant access to ${getInviteRecipientLabel(incomingInvite)}`
                      : "Skip for now"}
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    {incomingInviteAction === "accept"
                      ? `Completing onboarding will grant ${getInviteRecipientLabel(incomingInvite)} access to linked chart data.`
                      : "This invite remains pending and can be accepted later."}
                  </p>
                </div>
              </div>
            </div>
          )}

        </motion.div>
      </AnimatePresence>
    );
  }

  if (initializing) {
    return (
      <div className="min-h-full flex items-start justify-center p-4 pt-6 pb-8 sm:items-center bg-gradient-to-b from-background to-muted">
        <Card className="w-full max-w-lg p-8 space-y-4 text-center shadow-[var(--shadow-medium)]">
          <Loader2 className="h-7 w-7 animate-spin mx-auto text-primary" />
          <p className="text-sm text-muted-foreground">Loading your onboarding status...</p>
        </Card>
      </div>
    );
  }

  if (isAwaitingPartner && sex === "male") {
    return (
      <div className="min-h-full flex items-start justify-center p-4 pt-6 pb-8 sm:items-center bg-gradient-to-b from-background to-muted">
        <Card className="w-full max-w-lg p-8 space-y-6 shadow-[var(--shadow-medium)]">
          <div className="text-center space-y-2">
            <div className="inline-flex items-center justify-center w-11 h-11 rounded-full bg-primary/15 text-primary">
              <EnvelopeSimple className="h-5 w-5" />
            </div>
            <h2 className="text-2xl font-semibold">Awaiting wife's signup</h2>
            <p className="text-sm text-muted-foreground">
              We sent an invite to {pendingInvite?.invitee_email || spouseEmail}. You'll be able to continue once she creates and links her account.
            </p>
          </div>

          {pendingInvite?.expires_at && (
            <p className="text-xs text-muted-foreground text-center">
              Invite expires on {new Date(pendingInvite.expires_at).toLocaleDateString()}.
            </p>
          )}

          {error && (
            <div className="rounded-md bg-destructive/10 border border-destructive/30 text-destructive text-sm px-3 py-2">
              {error}
            </div>
          )}

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <Button
              variant="outline"
              onClick={handleResendInvite}
              disabled={loading || !pendingInvite}
              className="h-12 w-full"
            >
              {loading ? "Please wait..." : "Resend Invite"}
            </Button>
            <Button
              onClick={handleCheckConnection}
              disabled={loading}
              className="h-12 w-full"
            >
              Check Connection
            </Button>
            <Button
              variant="outline"
              onClick={handleUseDifferentEmail}
              disabled={loading}
              className="h-12 w-full"
            >
              Use Different Email
            </Button>
          </div>

          <div className="pt-1 text-center">
            <button
              type="button"
              onClick={handleSignOut}
              className="text-xs text-muted-foreground/80 hover:text-muted-foreground transition-colors"
            >
              Sign out
            </button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-full flex items-start justify-center p-4 pt-6 pb-8 sm:items-center bg-gradient-to-b from-background to-muted">
      <Card className="w-full max-w-lg p-8 space-y-6 shadow-[var(--shadow-medium)]">
        {/* Green progress bar */}
        <div className="w-full h-2 mb-6 bg-muted rounded-full overflow-hidden">
          <div
            className="h-full bg-green-500 transition-all duration-300"
            style={{ width: `${progressPercent}%` }}
          />
        </div>
        <div className="flex justify-center gap-1 mb-6">
          {Array.from({ length: totalSteps }).map((_, i) => (
            <div
              key={i}
              className={`h-2 rounded-full transition-all ${
                i + 1 === step
                  ? "w-8 bg-primary"
                  : i + 1 < step
                  ? "w-2 bg-primary"
                  : "w-2 bg-muted"
              }`}
            />
          ))}
        </div>
        {renderStep()}
        {error && (
          <div className="rounded-md bg-destructive/10 border border-destructive/30 text-destructive text-sm px-3 py-2">
            {error}
          </div>
        )}
        <div className="flex gap-3 pt-4">
          {step > 1 && (
            <Button
              variant="outline"
              onClick={handleBack}
              className="flex-1 h-12"
            >
              <ChevronLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
          )}
          <Button
            onClick={handleNext}
            disabled={loading}
            className="flex-1 h-12"
          >
            {loading
              ? "Please wait..."
              : step === totalSteps
              ? "Complete"
              : "Next"}
            {step < totalSteps && <ChevronRight className="h-4 w-4 ml-2" />}
          </Button>
        </div>
        <div className="pt-1 text-center">
          <button
            type="button"
            onClick={handleSignOut}
            className="text-xs text-muted-foreground/80 hover:text-muted-foreground transition-colors"
          >
            Sign out
          </button>
        </div>
      </Card>
    </div>
  );
};

export default Onboarding;
